import { icon } from "../icons.js";
import {
  iso,
  todayIso,
  addDays,
  diffNights,
  humanLong,
  humanChip,
  monthLabel,
  monthListBetween,
  buildMonthGrid,
  nightsWord,
  DOW,
} from "../lib/dates.js";
import { fmtRub, fmtFrom } from "../lib/money.js";
import { priceNote } from "../lib/price-note.js";
import { canCheckIn, minNightPrice, roomsTotal, nearestAvailable } from "../lib/avail.js";
import { outCandidates, calStep } from "../lib/datepick.js";

// Список месяцев календаря: от сегодня до горизонта
export function calendarMonths(state) {
  const from = todayIso();
  const to = addDays(from, state.config?.horizon ?? 180);
  return monthListBetween(from, to);
}

function dayCell(state, dateIso, candidates) {
  const today = todayIso();
  const { checkin, checkout } = state.dates;
  const adults = state.guests.adults;
  const idx = state.map;
  const day = Number(dateIso.slice(8, 10));

  const cell = (cls, inner, attrs = "") =>
    `<button type="button" class="okb-d ${cls}" data-iso="${dateIso}" ${attrs}><span class="okb-n">${day}</span>${inner}</button>`;
  const off = () =>
    `<button type="button" class="okb-d okb-off" disabled><span class="okb-n">${day}</span></button>`;

  if (dateIso < today) return off();

  // шаг «заезд» (T37): без дат — первый выбор; с датами (чип «Заезд») — новый заезд любой
  // доступной датой, выбранный диапазон остаётся виден, повторный тап по заезду — сброс
  if (calStep(state) === "in") {
    if (checkin && dateIso === checkin)
      return cell(
        "okb-pick",
        `<span class="okb-tag">заезд</span>`,
        `data-okb="cal-reset" aria-label="Дата заезда ${humanLong(dateIso)}. Нажмите, чтобы сбросить даты."`,
      );
    const action = checkin ? "move-in" : "pick-in";
    const ok = idx && canCheckIn(idx, dateIso, adults);
    if (checkout && dateIso === checkout)
      return cell(
        "okb-pick",
        `<span class="okb-tag">выезд</span>`,
        ok ? `data-okb="${action}" aria-label="${humanLong(dateIso)}: заезд с этой даты"` : "",
      );
    if (checkout && dateIso > checkin && dateIso < checkout)
      return cell(
        "okb-inband",
        "",
        ok ? `data-okb="${action}" aria-label="${humanLong(dateIso)}: заезд с этой даты"` : "",
      );
    if (ok) {
      const price = minNightPrice(idx, dateIso, adults);
      const low = roomsTotal(idx, dateIso, adults) <= 3 ? " okb-low" : "";
      return cell(
        `okb-av${low}`,
        price ? `<span class="okb-p">${fmtFrom(price)}</span>` : "",
        `data-okb="${action}" aria-label="${humanLong(dateIso)}, заезд возможен, от ${fmtRub(price ?? 0)} за ночь"`,
      );
    }
    // нет в карте: дата закрыта либо ещё не отсканирована — по /api/map их не различить
    return cell(
      "okb-soldout",
      "",
      `data-okb="alt" aria-label="${humanLong(dateIso)}, заезда нет — показать ближайшие даты"`,
    );
  }

  // Шаг «выезд» (T37): справа от заезда — выезды, в том числе смена уже выбранного; слева —
  // перенос заезда. Сброс — повторный тап по заезду.
  if (dateIso === checkin)
    return cell(
      "okb-pick",
      `<span class="okb-tag">заезд</span>`,
      `data-okb="cal-reset" aria-label="Дата заезда ${humanLong(dateIso)}. Нажмите, чтобы сбросить даты."`,
    );
  if (dateIso < checkin) {
    if (idx && canCheckIn(idx, dateIso, adults))
      return cell(
        "okb-movein",
        "",
        `data-okb="move-in" aria-label="${humanLong(dateIso)}: перенести заезд на эту дату"`,
      );
    return cell(
      "okb-soldout",
      "",
      `data-okb="alt" aria-label="${humanLong(dateIso)}, заезда нет — показать ближайшие даты"`,
    );
  }
  if (dateIso === checkout)
    return cell(
      "okb-pick",
      `<span class="okb-tag">выезд</span>`,
      `aria-label="Дата выезда ${humanLong(dateIso)}. Другой выезд — нажмите на нужную дату."`,
    );
  const inband = Boolean(checkout) && dateIso < checkout;
  const cand = candidates?.get(dateIso);
  if (cand) {
    const what = `Выезд ${humanLong(dateIso)}, ${cand.L} ${nightsWord(cand.L)}`;
    // внутри выбранного диапазона — полоса без цены, тап укорачивает проживание
    if (inband) return cell("okb-inband", "", `data-okb="pick-out" aria-label="${what}"`);
    // minTotal null = кандидат из карты или /api/checkouts, живая цена ещё в пути
    if (cand.minTotal == null)
      return cell(
        "okb-cand",
        `<span class="okb-p okb-pending">…</span>`,
        `data-okb="pick-out" aria-label="${what}, цена уточняется"`,
      );
    return cell(
      "okb-cand",
      `<span class="okb-p">${fmtFrom(cand.minTotal)}</span>`,
      `data-okb="pick-out" aria-label="${what}, от ${fmtRub(cand.minTotal)}"`,
    );
  }
  // недоступный выезд: серый, тап показывает причину под календарём и выбор не трогает
  return cell(
    inband ? "okb-inband" : "okb-soldout",
    "",
    `data-okb="why" aria-label="${humanLong(dateIso)}: выезд недоступен — показать причину"`,
  );
}

// Модалка «Выберите даты» (ref_2, но наш календарь не слепой)
export function renderDatesModal(state) {
  if (!state.map) {
    return `
    <div class="okb-modal" role="dialog" aria-modal="true" aria-label="Выбор дат">
      <div class="okb-mhead">
        <div><h3>Выберите даты</h3></div>
        <button type="button" class="okb-mclose" data-okb="close-modal" aria-label="Закрыть">${icon("close")}</button>
      </div>
      <div class="okb-spin">Загружаем календарь…</div>
    </div>`;
  }
  const months = calendarMonths(state);
  const mi = Math.min(state.ui.monthIndex, months.length - 1);
  const [y, m] = months[mi];
  const { shift, days } = buildMonthGrid(y, m);
  const { checkin, checkout } = state.dates;
  const step = calStep(state);

  // Кандидаты выезда (T37): сразу по локальной карте, затем /api/checkouts (без цен, ячейки
  // с «…»), с приходом /api/lengths — живые цены; не подтверждённые ценами даты гаснут.
  // Считаются и при выбранных обеих датах — тап справа от заезда меняет выезд.
  let candidates = null;
  let body = "";
  if (step === "out") {
    const picked = outCandidates(state);
    if (picked) {
      candidates = new Map(picked.list.map((c) => [c.to, c]));
      if (picked.pending && state.lengths.loading)
        body = `<div class="okb-spin">Загружаем цены…</div>`;
      else if (picked.pending && state.lengths.error)
        body = `<div class="okb-notice okb-error">${icon("warn")}<span>${state.lengths.error}</span></div>`;
      else if (candidates.size === 0)
        body = `<div class="okb-calalt">С заезда ${humanLong(checkin)} нет разрешённых вариантов проживания — выберите другую дату заезда.</div>`;
    } else if (state.lengths.loading || state.checkouts.loading) {
      // checkouts пуст или ещё в пути — его «пусто» не приговор, ждём живые длины
      body = `<div class="okb-spin">Считаем цены и доступные длины…</div>`;
    } else if (state.lengths.error) {
      body = `<div class="okb-notice okb-error">${icon("warn")}<span>${state.lengths.error}</span></div>`;
    }
  }

  let grid = DOW.map((d) => `<div class="okb-dow">${d}</div>`).join("");
  grid += "<div></div>".repeat(shift);
  for (let d = 1; d <= days; d++) grid += dayCell(state, iso(y, m, d), candidates);

  // подсказка ближайших дат по клику на закрытой дате; при выбранном заезде — перенос заезда
  let alt = "";
  if (state.ui.altFor) {
    const found = nearestAvailable(state.map, state.ui.altFor, state.guests.adults);
    const action = checkin ? "move-in" : "pick-in";
    alt = `<div class="okb-calalt"><b>${humanLong(state.ui.altFor)}</b> — заезда нет.`;
    if (found.length) {
      alt += ` Ближайшие свободные даты:<div class="okb-alts">${found
        .map(
          (f) =>
            `<button type="button" class="okb-altbtn" data-okb="${action}" data-iso="${f}">${humanLong(f)}<small>от ${fmtRub(minNightPrice(state.map, f, state.guests.adults) ?? 0)} за ночь</small></button>`,
        )
        .join("")}</div>`;
    } else alt += " Рядом свободных заездов не нашлось.";
    alt += "</div>";
  }

  const legend =
    step === "in"
      ? `<span><i class="okb-sw okb-in"></i>заезд возможен, минимальная цена за ночь${priceNote(state.config)?.legendSuffix ?? ""}</span><span><i class="okb-sw okb-no"></i>заезда нет</span>`
      : `<span><i class="okb-sw okb-out"></i>выезд, стоимость за всё проживание</span>`;

  // строка над кнопкой (T37) по умолчанию пустая — только сообщения: причина недоступной даты
  // и «Проверяем выезд…» (после задержки, shown); aria-live — читалка объявит их после тапа
  const message = state.ui.why?.shown ? state.ui.why.text : "";
  const nights = checkin && checkout ? diffNights(checkin, checkout) : 0;
  const cta = nights ? `Продолжить · ${nights} ${nightsWord(nights)}` : "Продолжить";

  // чипы над календарём (T37): какой шаг идёт и что выбрано; тап — сменить шаг
  const chip = (mode, label, value, enabled) =>
    `<button type="button" class="okb-chip${step === mode ? " okb-on" : ""}" data-okb="mode-${mode}" aria-pressed="${step === mode}" ${enabled ? "" : "disabled"}><span>${label}:</span> ${value ?? "—"}</button>`;
  const chips = `<div class="okb-chips">${chip("in", "Заезд", checkin && humanChip(checkin), true)}${chip("out", "Выезд", checkout && humanChip(checkout), Boolean(checkin))}</div>`;

  return `
  <div class="okb-modal" role="dialog" aria-modal="true" aria-label="Выбор дат">
    <div class="okb-mhead">
      <div><h3>Выберите даты</h3></div>
      <button type="button" class="okb-mclose" data-okb="close-modal" aria-label="Закрыть">${icon("close")}</button>
    </div>
    ${chips}
    <div class="okb-calhead">
      <div class="okb-mname">${monthLabel(months[mi])}</div>
      <div class="okb-nav">
        <button type="button" data-okb="cal-prev" aria-label="Предыдущий месяц" ${mi <= 0 ? "disabled" : ""}>‹</button>
        <button type="button" data-okb="cal-next" aria-label="Следующий месяц" ${mi >= months.length - 1 ? "disabled" : ""}>›</button>
      </div>
    </div>
    <div class="okb-grid">${grid}</div>
    <div class="okb-legend">${legend}</div>
    ${alt}
    ${body}
    <div class="okb-mfoot">
      <div class="okb-mstatus okb-calmsg" role="status" aria-live="polite">${message}</div>
      <button type="button" class="okb-primary" data-okb="confirm-dates" ${nights ? "" : "disabled"}>${cta}</button>
    </div>
  </div>`;
}
